import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { methods, canUseBackendProxy, enableBackendProxy } from "../../../utils/axios";
import { TestTypeService } from "../../../utils/test-type-registry";
import DiscTest from "./disc-test";
import CompetenceTest from "./competence-test";
import UnifiedTest from "./unified-test";

// Sin límite, un pedido que la red deja colgado (firewall de empresa) dejaba la
// pantalla en "Cargando..." para siempre.
const LOAD_TIMEOUT_MS = 20000;

// Sin respuesta del back (red, bloqueo o timeout), a diferencia de un error HTTP
// que sí respondió (link inválido, test eliminado).
const isConnectionError = (error) => !error?.response;

const fetchTest = (collaboratorTestID) =>
  methods.post('/collaborator_tests', { collaboratorTestID }, { timeout: LOAD_TIMEOUT_MS });

// Si el back no responde, se reintenta por el proxy del mismo dominio del front.
// Si así funciona, el resto del test (sesión, respuestas) sigue por el proxy.
const loadTest = async (collaboratorTestID) => {
  try {
    return await fetchTest(collaboratorTestID);
  } catch (error) {
    if (!isConnectionError(error) || !canUseBackendProxy()) throw error;
    console.warn('El back no respondió, reintentando por el proxy del front', error);
    enableBackendProxy();
    return fetchTest(collaboratorTestID);
  }
};

const Test = () => {

  const [test, setTest] = useState();
  const [loadFailed, setLoadFailed] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [searchParams] = useSearchParams();
  const collaboratorTestID = searchParams.get('id');

  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    setConnectionFailed(false);
    if (!collaboratorTestID) {
      setLoadFailed(true);
      return;
    }
    loadTest(collaboratorTestID)
      .then((res) => {
        if (cancelled) return;
        if (res.data) {
          setTest({ ...res.data, collaboratorTestID });
        } else {
          setLoadFailed(true);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.log(error);
        if (isConnectionError(error)) {
          setConnectionFailed(true);
        } else {
          setLoadFailed(true);
        }
      });
    return () => { cancelled = true; };
  }, [collaboratorTestID, attempt]);

  const getTest = () => {
    if (connectionFailed) {
      return (
        <div className="test-loading test-load-error">
          <h1>No pudimos conectarnos</h1>
          <p>
            Tu conexión no logró comunicarse con nuestra plataforma. Esto suele pasar
            en redes de empresa, que bloquean algunos sitios.
          </p>
          <ul>
            <li>Prueba abrir el link desde tu celular usando datos móviles (no el wifi de la empresa).</li>
            <li>O conéctate a otra red, como la de tu casa.</li>
            <li>Si usas VPN, desactívala y vuelve a intentar.</li>
            <li>También puedes probar con otro navegador (Chrome, Edge, Safari o Firefox).</li>
          </ul>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Reintentar
          </button>
          <p>
            Si el problema continúa, contacta a la persona que te envió la prueba.
          </p>
        </div>
      );
    }

    // Antes, si la carga fallaba (link cortado, test eliminado, back caído) la
    // pantalla quedaba en "Cargando..." para siempre.
    if (loadFailed) {
      return (
        <div className="test-loading test-load-error">
          <h1>No pudimos cargar la prueba</h1>
          <p>
            Es posible que el link esté incompleto o que ya no sea válido.
            Cierra esta pestaña y vuelve a entrar desde el link del correo que recibiste.
          </p>
          <p>
            Si el problema continúa, contacta a la persona que te envió la prueba.
          </p>
        </div>
      );
    }

    if (!test) {
      return (
        <div className="test-loading">
          <h1>Cargando...</h1>
        </div>
      );
    }

    if (test.results) {
      return (
        <div className="test-loading">
          <h1>La prueba ya fue respondida</h1>
        </div>
      );
    }

    const config = TestTypeService.getTestConfigByTest(test);

    if (config?.category === 'competency') {
      return <CompetenceTest test={test} />;
    }

    if (config?.category === 'unified') {
      return <UnifiedTest test={test} />;
    }

    if (config?.category === 'disc' || config?.category === 'bounded-disc') {
      return <DiscTest test={test} isBoundedTest={config.category === 'bounded-disc'} />;
    }

    return (
      <div className="test-loading">
        <h1>Tipo de test no manejado</h1>
      </div>
    );
  };

  return (<>
    {
      getTest()
    }
  </>);
}

export default Test;